type Response = { success: boolean; data?: any };
type Group<Member> = {
  serviceUserId: string;
  serviceUserName: string;
  members: Member[];
  totalUnread: number;
};

// Page size bounds concurrent member lookups; it is not a total-person limit.
export async function loadFamilyChatSidebar<
  Member extends { unreadCount?: number },
>({
  getServiceUsers,
  listMembers,
  normalizeMembers,
  onGroup,
  isCurrent = () => true,
}: {
  getServiceUsers: (query: {
    page: number;
    limit: number;
    query: string;
  }) => Promise<Response>;
  listMembers: (serviceUserId: string) => Promise<Response>;
  normalizeMembers: (data: any) => Member[];
  onGroup?: (group: Group<Member>) => void;
  isCurrent?: () => boolean;
}): Promise<Group<Member>[]> {
  const limit = 25;
  const groups: Group<Member>[] = [];
  const seen = new Set<string>();
  for (let page = 1; isCurrent(); page++) {
    const response = await getServiceUsers({ page, limit, query: "" });
    if (!isCurrent()) return [];
    if (!response.success || !Array.isArray(response.data))
      throw new Error("We couldn’t load all family chats. Try again.");
    const people = response.data.filter((person) => {
      const id = String(person?._id || person?.id || "");
      return id && !seen.has(id);
    });
    if (response.data.length && !people.length)
      throw new Error("We couldn’t load all family chats. Try again.");
    const batch = await Promise.all(
      people.map(async (person) => {
        const serviceUserId = String(person._id || person.id);
        seen.add(serviceUserId);
        const result = await listMembers(serviceUserId);
        if (!result.success)
          throw new Error("We couldn’t load all family chats. Try again.");
        const members = normalizeMembers(result.data);
        const group = {
          serviceUserId,
          serviceUserName: person.name || "Service User",
          members,
          totalUnread: members.reduce(
            (total, member) => total + (member.unreadCount || 0),
            0
          ),
        };
        if (isCurrent() && members.length) onGroup?.(group);
        return group;
      })
    );
    if (!isCurrent()) return [];
    groups.push(...batch.filter((group) => group.members.length));
    if (response.data.length < limit) return groups;
  }
  return [];
}
